import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as jobService from "../services/jobService";
import * as productService from "../services/productService";
import { useJobMutations } from "./useJobMutations";
import { useJobReceipts } from "./useJobReceipts";
import { useJobProducts } from "./useJobProducts";
import { message } from "antd";

const useJobs = () => {
  const queryClient = useQueryClient();

  // Core state
  const [editingJob, setEditingJob] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  const [showJobDetailModal, setShowJobDetailModal] = useState(false);

  // Tab state for All Orders vs Needs Resupply
  const [activeTab, setActiveTab] = useState("all"); // "all" or "resupply"

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Determine sortBy based on active tab
  const sortBy = activeTab === "resupply" ? "oldestFirst" : null;
  // For resupply tab, exclude completed orders
  const effectiveStatusFilter = activeTab === "resupply" ? "PENDING" : statusFilter;

  // Fetch jobs data with dynamic parameters
  const { data, isLoading, error } = useQuery({
    queryKey: ["jobs", { searchTerm, page, pageSize, statusFilter: effectiveStatusFilter, sortBy, activeTab }],
    queryFn: () => jobService.getJobsList({
      searchTerm,
      page,
      pageSize,
      statusFilter: effectiveStatusFilter === "All" ? "All" : effectiveStatusFilter,
      sortBy
    }),
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 1, // Retry failed requests once
  });

  // Fetch products data
  const { data: products, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ["products"],
    queryFn: () => productService.getProducts(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Retry failed requests once
  });

  const jobs = data?.jobs || [];

  // Reset page when tab changes
  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  // Use separated hooks
  const mutations = useJobMutations(selectedJobForDetails, setSelectedJobForDetails, searchTerm, page, pageSize, effectiveStatusFilter);
  const receipts = useJobReceipts(queryClient, jobs, selectedJobForDetails, setSelectedJobForDetails);
  const jobProductsHook = useJobProducts();
  


  // Mobile detection
  useEffect(() => {
    const isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    setIsMobile(isMobileDevice);
  }, []);
  


  // Job editing functions
  const startEditing = (job) => {
    setEditingJob(job);
  };

  const cancelEditing = () => {
    setEditingJob(null);
  };

  const handleJobUpdate = (updatedJob, materialData = null) => {
    // If material data is provided, handle it first
    if (materialData && materialData.source === "Barcode Scan") {
      // For barcode flow, avoid optimistic job overwrite that can temporarily hide materials
      jobProductsHook.handleBarcodeProduct(updatedJob.id, materialData, mutations.addMaterialToJob, selectedJobForDetails, setSelectedJobForDetails, queryClient, searchTerm, page, pageSize, effectiveStatusFilter);
      return;
    }

    mutations.updateJob.mutate(updatedJob, {
      onSuccess: () => {
        // Update the local state to reflect the changes immediately
        setSelectedJobForDetails(updatedJob);
        // Also update the jobs list if this job is in it
        if (jobs.find(job => job.id === updatedJob.id)) {
          const updatedJobs = jobs.map(job =>
            job.id === updatedJob.id ? updatedJob : job
          );
          // Force a re-render by updating the query data
          try {
            queryClient.setQueryData(["jobs", { searchTerm, page, pageSize, statusFilter: effectiveStatusFilter, sortBy, activeTab }], {
              ...data,
              jobs: updatedJobs
            });
          } catch (error) {
            console.error("Failed to update query data:", error);
            // Don't let this error break the flow
          }
        }
        // Skip invalidating queries to preserve job context
        console.log("Skipping query invalidation in handleJobUpdate to preserve job context");
      },
      onError: (error) => {
        console.error("Failed to update job:", error);
        // Don't let the job disappear on error
      }
    });
    setEditingJob(null);
  };

  // View job details
  const viewJobDetails = async (job) => {
    // Always fetch fresh job data from server to ensure we have the latest state
    // This prevents showing stale data after deletions/updates
    setShowJobDetailModal(true); // Show modal immediately for better UX
    try {
      const freshJob = await jobService.getJob(job.id);
      setSelectedJobForDetails(freshJob);
    } catch (error) {
      console.error("Failed to fetch fresh job data, using cached:", error);
      // Fallback to cached job if fetch fails
      setSelectedJobForDetails(job);
      // Show error message if fetch fails
      if (error.response?.status !== 404) {
        message.warning("Could not refresh job data. Showing cached version.");
      }
    }
  };

  // Receipt verification
  const handleReceiptVerification = async (receiptData) => {
    try {
      // Add materials from the receipt to the job
      const currentJob = selectedJobForDetails;
      
      if (receiptData.items && receiptData.items.length > 0 && currentJob) {
          // Convert receipt items to materials and add them to the job.
          // Reuse an existing product when possible (match by name + close price) so
          // uploading the same receipt twice or the same SKU on multiple receipts
          // doesn't pollute the Products catalog with duplicates.
          for (const item of receiptData.items) {
            if (item.name && item.price) {
              try {
                let productToUse = null;

                try {
                  const existingProducts = await productService.searchProductsByName(item.name);
                  if (Array.isArray(existingProducts) && existingProducts.length > 0) {
                    const targetPrice = Number(item.price) || 0;
                    const exactMatch = existingProducts.find(p =>
                      (p.name || "").toLowerCase() === item.name.toLowerCase() &&
                      Math.abs(Number(p.unitPrice || p.wholesalePrice || 0) - targetPrice) < 0.01
                    );
                    productToUse = exactMatch || existingProducts.find(p =>
                      (p.name || "").toLowerCase() === item.name.toLowerCase()
                    );
                  }
                } catch (searchErr) {
                  console.warn("Product search failed; will create new:", searchErr);
                }

                if (!productToUse) {
                  const productData = {
                    name: item.name,
                    description: `Product from receipt: ${item.name}`,
                    unitPrice: item.price,
                    category: "Receipt Item",
                    supplier: receiptData.vendor || "Receipt"
                  };
                  productToUse = await productService.createProduct(productData);
                  queryClient.invalidateQueries({ queryKey: ["products"] });
                }

                const materialDto = {
                  productId: productToUse.id,
                  quantity: item.quantity || 1,
                  unitPrice: item.price,
                  notes: `From receipt: ${receiptData.receipt_number || 'Unknown'}`
                };

                await mutations.addMaterialToJob.mutateAsync({
                  jobId: currentJob.id,
                  material: materialDto
                });

              } catch (itemError) {
                console.error(`Failed to process item ${item.name}:`, itemError);
              }
            }
          }
          
          // Simple refresh - just invalidate jobs queries
          queryClient.invalidateQueries({ 
            queryKey: ["jobs"], 
            exact: false 
          });
      }
      
      // Close the verification modal - simple!
      receipts.setShowVerificationModal(false);
      
      console.log("Receipt verification completed - modal closed");
      return { success: true };
      
    } catch (error) {
      console.error("Error adding materials:", error);
      // Still close the modal even if there's an error
      receipts.setShowVerificationModal(false);
      throw error;
    }
  };



  return {
    // Core data
    jobs,
    filteredJobs: jobs, // Jobs are already filtered by the API
    isLoading,
    error,

    // Tab state
    activeTab,
    setActiveTab,

    // Filters
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    page,
    setPage,
    pageSize,
    setPageSize,

    // Mutations
    ...mutations,

    // Receipts
    ...receipts,

    // Products (job products management)
    ...jobProductsHook,

    // Job editing
    editingJob,
    startEditing,
    cancelEditing,
    handleJobUpdate,

    // Job details
    selectedJobForDetails,
    setSelectedJobForDetails,
    showJobDetailModal,

    // Products
    products,
    productsLoading,
    productsError,
    setShowJobDetailModal,
    viewJobDetails,

    // Receipt verification
    handleReceiptVerification,

    // Pagination
    totalPages: data?.totalPages || 1,
    totalJobs: data?.totalJobs || 0,
    hasNext: data?.hasNext || false,
    hasPrevious: data?.hasPrevious || false,

    // Mobile
    isMobile,
  };
};

export default useJobs;